// src/components/CapoOfficina/CapoOfficinaMezzi.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query, 
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../assets/logo.png';
import '../Admin/Cantieri/MezziManagement.css';

const CapoOfficinaMezzi = () => {
  const [mezzi, setMezzi] = useState([]);
  const [filteredMezzi, setFilteredMezzi] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProprieta, setFilterProprieta] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMezzo, setSelectedMezzo] = useState(null);
  
  // Stati per storico MANUTENZIONI
  const [showStoricoManutenzioniModal, setShowStoricoManutenzioniModal] = useState(false);
  const [storicoManutenzioni, setStoricoManutenzioni] = useState([]);
  const [loadingStoricoManutenzioni, setLoadingStoricoManutenzioni] = useState(false);
  const [dataInizioManutenzioni, setDataInizioManutenzioni] = useState('');
  const [dataFineManutenzioni, setDataFineManutenzioni] = useState('');
  
  // Stati per storico UTILIZZO CANTIERI
  const [showStoricoUtilizzoModal, setShowStoricoUtilizzoModal] = useState(false);
  const [dataInizioUtilizzo, setDataInizioUtilizzo] = useState('');
  const [dataFineUtilizzo, setDataFineUtilizzo] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);
  
  // Stati per form creazione/modifica mezzo
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nomeMezzo: '',
    targa: '',
    proprieta: '',
    note: ''
  });

  // Opzioni proprietà
  const proprietaOptions = [
    'Isap',
    'Fd', 
    'Stradedg',
    'Eurosafe',
    'Noleggio'
  ];

  useEffect(() => {
    loadMezzi();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [mezzi, searchTerm, filterProprieta]);

  const loadMezzi = async () => {
    try {
      setIsLoading(true);
      const mezziQuery = query(
        collection(db, 'mezzi'),
        orderBy('nomeMezzo', 'asc')
      );
      
      const snapshot = await getDocs(mezziQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMezzi(data);
      console.log(`Caricati ${data.length} mezzi`);
    } catch (error) {
      console.error("Errore nel caricamento mezzi:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...mezzi];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(mezzo => 
        mezzo.nomeMezzo.toLowerCase().includes(search) ||
        (mezzo.targa && mezzo.targa.toLowerCase().includes(search)) ||
        (mezzo.note && mezzo.note.toLowerCase().includes(search))
      );
    }

    if (filterProprieta !== 'all') {
      filtered = filtered.filter(mezzo => mezzo.proprieta === filterProprieta);
    }

    setFilteredMezzi(filtered);
  };

  // Reset del form
  const resetForm = () => {
    setFormData({
      nomeMezzo: '',
      targa: '',
      proprieta: '',
      note: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Gestione input form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Salvataggio mezzo (creazione o modifica)
  const saveMezzo = async (e) => {
    e.preventDefault();
    
    if (!formData.nomeMezzo.trim()) {
      alert('Il nome del mezzo è obbligatorio');
      return;
    }

    try {
      setIsLoading(true);
      
      const mezzoData = {
        nomeMezzo: formData.nomeMezzo.trim(),
        targa: formData.targa.trim(),
        proprieta: formData.proprieta,
        note: formData.note.trim(),
        updatedAt: new Date()
      };

      if (editingId) {
        const mezzoRef = doc(db, 'mezzi', editingId);
        await updateDoc(mezzoRef, mezzoData);
        console.log('Mezzo aggiornato:', editingId);
        alert('✅ Mezzo aggiornato con successo!');
      } else {
        mezzoData.createdAt = new Date();
        await addDoc(collection(db, 'mezzi'), mezzoData);
        console.log('Nuovo mezzo creato');
        alert('✅ Mezzo creato con successo!');
      }

      resetForm();
      await loadMezzi();
    } catch (error) {
      console.error("Errore nel salvataggio mezzo:", error);
      alert('❌ Errore nel salvataggio del mezzo');
    } finally {
      setIsLoading(false);
    }
  };

  // Apertura form per modifica
  const editMezzo = (mezzo) => {
    setFormData({
      nomeMezzo: mezzo.nomeMezzo,
      targa: mezzo.targa || '',
      proprieta: mezzo.proprieta || '',
      note: mezzo.note || ''
    });
    setEditingId(mezzo.id);
    setShowForm(true);
  };

  // Eliminazione mezzo
  const deleteMezzo = async (mezzoId, nomeMezzo) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il mezzo "${nomeMezzo}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      const mezzoRef = doc(db, 'mezzi', mezzoId);
      await deleteDoc(mezzoRef);
      console.log('Mezzo eliminato:', mezzoId);
      await loadMezzi();
      alert('✅ Mezzo eliminato con successo!');
    } catch (error) {
      console.error("Errore nell'eliminazione mezzo:", error);
      alert('❌ Errore nell\'eliminazione del mezzo');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // STORICO MANUTENZIONI
  // ============================================
  
  const openStoricoManutenzioniModal = (mezzo) => {
    setSelectedMezzo(mezzo);
    setShowStoricoManutenzioniModal(true);
    
    // Imposta date di default (ultimo mese)
    const oggi = new Date();
    const primoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth(), 1);
    setDataInizioManutenzioni(primoGiornoMese.toISOString().split('T')[0]);
    setDataFineManutenzioni(oggi.toISOString().split('T')[0]);
  };

  const closeStoricoManutenzioniModal = () => {
    setShowStoricoManutenzioniModal(false);
    setSelectedMezzo(null);
    setStoricoManutenzioni([]);
    setDataInizioManutenzioni('');
    setDataFineManutenzioni('');
  };

  const caricaStoricoManutenzioni = async () => {
    if (!selectedMezzo || !dataInizioManutenzioni || !dataFineManutenzioni) {
      alert('⚠️ Seleziona date valide');
      return;
    }

    setLoadingStoricoManutenzioni(true);
    
    try {
      const startDate = new Date(dataInizioManutenzioni);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dataFineManutenzioni);
      endDate.setHours(23, 59, 59, 999);

      // Cerca nei rapportini officina le manutenzioni per questo mezzo
      const rapportiniQuery = query(
        collection(db, 'rapportiniOfficina'),
        where('stato', '==', 'Attivo')
      );
      
      const snapshot = await getDocs(rapportiniQuery);
      const manutenzioni = [];
      
      snapshot.forEach(doc => {
        const rapportino = doc.data();
        const dataRapportino = rapportino.data?.toDate ? rapportino.data.toDate() : new Date(rapportino.data);
        
        // Verifica se il rapportino è nel periodo
        if (dataRapportino >= startDate && dataRapportino <= endDate) {
          // Controlla se questo rapportino contiene lavorazioni per il mezzo selezionato
          if (rapportino.lavorazioni && Array.isArray(rapportino.lavorazioni)) {
            rapportino.lavorazioni.forEach(lav => {
              if (lav.mezzoId === selectedMezzo.id) {
                manutenzioni.push({
                  id: doc.id,
                  data: dataRapportino,
                  tipoLavorazione: lav.tipoLavorazione || 'N/D',
                  tempoImpiegato: lav.oreInvestite || 0,
                  personaleImpiegato: lav.personaleAssegnato || [],
                  km: lav.kmMezzo || 'N/D',
                  note: lav.note || ''
                });
              }
            });
          }
        }
      });
      
      // Ordina per data (più recenti prima)
      manutenzioni.sort((a, b) => b.data - a.data);
      
      setStoricoManutenzioni(manutenzioni);
      console.log(`Trovate ${manutenzioni.length} manutenzioni per ${selectedMezzo.nomeMezzo} nel periodo`);
    } catch (error) {
      console.error('Errore nel caricamento storico manutenzioni:', error);
      setStoricoManutenzioni([]);
      alert('❌ Errore nel caricamento dello storico manutenzioni');
    } finally {
      setLoadingStoricoManutenzioni(false);
    }
  };

  // ============================================
  // STORICO UTILIZZO CANTIERI
  // ============================================
  
  const openStoricoUtilizzoModal = (mezzo) => {
    setSelectedMezzo(mezzo);
    setShowStoricoUtilizzoModal(true);
    
    // Imposta date di default (ultimo mese)
    const oggi = new Date();
    const unMeseFa = new Date();
    unMeseFa.setMonth(oggi.getMonth() - 1);
    setDataFineUtilizzo(oggi.toISOString().split('T')[0]);
    setDataInizioUtilizzo(unMeseFa.toISOString().split('T')[0]);
  };

  const closeStoricoUtilizzoModal = () => {
    setShowStoricoUtilizzoModal(false);
    setSelectedMezzo(null);
    setDataInizioUtilizzo('');
    setDataFineUtilizzo('');
  };

  // Funzione per recuperare i rapportini del mezzo nel periodo
  const getRapportiniMezzo = async (mezzoId, mezzoNome, dataInizio, dataFine) => {
    try {
      const rapportiniRef = collection(db, 'rapportini');
      const q = query(
        rapportiniRef,
        where('stato', '==', 'Attivo')
      );

      const snapshot = await getDocs(q);
      const rapportini = [];

      const startDate = new Date(dataInizio);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dataFine);
      endDate.setHours(23, 59, 59, 999);

      // Carica i cantieri per recuperare i nomi
      const cantieriRef = collection(db, 'cantieri');
      const cantieriSnapshot = await getDocs(cantieriRef);
      const cantieriMap = new Map();
      cantieriSnapshot.forEach(doc => {
        const cantiereData = doc.data();
        cantieriMap.set(doc.id, {
          id: doc.id,
          nome: cantiereData.nomeCommessa || cantiereData.nomeCantiere || 'Cantiere Sconosciuto',
          ...cantiereData
        });
      });

      snapshot.forEach((doc) => {
        const data = doc.data();
        
        // Gestione data del rapportino
        let rapportinoDate;
        if (data.data?.toDate && typeof data.data.toDate === 'function') {
          rapportinoDate = data.data.toDate();
        } else if (data.data?.seconds) {
          rapportinoDate = new Date(data.data.seconds * 1000);
        } else if (data.data instanceof Date) {
          rapportinoDate = data.data;
        } else if (typeof data.data === 'string') {
          rapportinoDate = new Date(data.data);
        } else if (typeof data.data === 'number') {
          rapportinoDate = new Date(data.data);
        } else {
          console.warn('Rapportino senza data valida:', doc.id);
          return;
        }

        // Verifica che la data sia valida
        if (isNaN(rapportinoDate.getTime())) {
          console.warn('Data non valida per rapportino:', doc.id);
          return;
        }

        // Crea una stringa data nel formato YYYY-MM-DD dalla data corretta
        const dataString = rapportinoDate.toISOString().split('T')[0];

        // Verifica se il rapportino è nel periodo
        if (rapportinoDate >= startDate && rapportinoDate <= endDate) {
          // Verifica se il mezzo è presente nel rapportino
          if (data.mezziUtilizzati && Array.isArray(data.mezziUtilizzati)) {
            const mezzoNelRapportino = data.mezziUtilizzati.find(m => 
              m.mezzoId === mezzoId || m.nome === mezzoNome || m.nomeMezzo === mezzoNome
            );

            if (mezzoNelRapportino) {
              // Recupera il nome del cantiere dalla mappa
              const cantiere = cantieriMap.get(data.cantiereId);
              const cantierNome = cantiere?.nome || data.cantierNome || 'Cantiere Sconosciuto';

              rapportini.push({
                id: doc.id,
                dataCorretta: dataString,
                data: rapportinoDate,
                cantiereId: data.cantiereId,
                cantierNome: cantierNome,
                mezzo: mezzoNelRapportino
              });
            }
          }
        }
      });

      // Ordina i rapportini per data corretta
      rapportini.sort((a, b) => a.dataCorretta.localeCompare(b.dataCorretta));

      console.log(`Trovati ${rapportini.length} rapportini per il mezzo nel periodo`);
      return rapportini;
    } catch (error) {
      console.error("Errore nel recupero rapportini mezzo:", error);
      throw error;
    }
  };

  // Funzione per generare PDF storico utilizzo cantieri
  const generaStoricoPDF = async () => {
    if (!selectedMezzo || !dataInizioUtilizzo || !dataFineUtilizzo) {
      alert('⚠️ Seleziona date valide');
      return;
    }

    try {
      setLoadingPDF(true);

      const rapportini = await getRapportiniMezzo(
        selectedMezzo.id,
        selectedMezzo.nomeMezzo,
        dataInizioUtilizzo,
        dataFineUtilizzo
      );

      if (rapportini.length === 0) {
        alert('ℹ️ Nessun rapportino trovato per il mezzo nel periodo selezionato');
        return;
      }

      // Crea PDF
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      let yPos = 20;

      // Converti logo in base64
      const logoBase64 = await getLogoBase64();

      // Header con logo
      if (logoBase64) {
        pdf.addImage(logoBase64, 'PNG', 15, 10, 40, 15);
      }

      // Titolo
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('STORICO UTILIZZO MEZZO', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Informazioni mezzo
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Mezzo: ${selectedMezzo.nomeMezzo}`, 15, yPos);
      yPos += 7;
      if (selectedMezzo.targa) {
        pdf.text(`Targa: ${selectedMezzo.targa}`, 15, yPos);
        yPos += 7;
      }
      pdf.text(`Proprietà: ${selectedMezzo.proprieta || 'N/A'}`, 15, yPos);
      yPos += 10;

      // Periodo
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Periodo: ${new Date(dataInizioUtilizzo).toLocaleDateString('it-IT')} - ${new Date(dataFineUtilizzo).toLocaleDateString('it-IT')}`, 15, yPos);
      yPos += 10;

      // Raggruppa per cantiere
      const rapportiniPerCantiere = rapportini.reduce((acc, r) => {
        if (!acc[r.cantiereId]) {
          acc[r.cantiereId] = {
            nome: r.cantierNome,
            rapportini: []
          };
        }
        acc[r.cantiereId].rapportini.push(r);
        return acc;
      }, {});

      // Calcoli totali
      let oreTotali = 0;

      // Tabella riepilogo per cantiere
      const tableData = [];
      
      Object.values(rapportiniPerCantiere).forEach(cantiere => {
        const rapportiniPerData = {};
        
        cantiere.rapportini.forEach(r => {
          const dataKey = r.dataCorretta;
          if (!rapportiniPerData[dataKey]) {
            rapportiniPerData[dataKey] = {
              ore: 0
            };
          }
          rapportiniPerData[dataKey].ore += parseFloat(r.mezzo.ore || r.mezzo.oreLavorate || 0);
        });
        
        const giorniUnici = Object.keys(rapportiniPerData).length;
        const oreCantiere = Object.values(rapportiniPerData).reduce((sum, d) => sum + d.ore, 0);
        
        oreTotali += oreCantiere;
        
        tableData.push([
          cantiere.nome,
          giorniUnici.toString(),
          oreCantiere.toFixed(1) + ' h'
        ]);
      });

      // Aggiungi riga totali
      tableData.push([
        { content: 'TOTALE', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
        { content: '', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
        { content: oreTotali.toFixed(1) + ' h', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
      ]);

      // Riepilogo per cantiere
      autoTable(pdf, {
        startY: yPos,
        head: [['Cantiere', 'N° Giorni', 'Ore Lavorate']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [0, 123, 255], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: 35, halign: 'center' },
          2: { cellWidth: 40, halign: 'right' }
        }
      });

      yPos = pdf.lastAutoTable.finalY + 15;

      // Nuova pagina per dettaglio giornaliero
      pdf.addPage();
      yPos = 20;

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DETTAGLIO GIORNALIERO', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Tabella dettaglio giornaliero - aggrega per data/cantiere
      const rapportiniAggregati = {};
      
      rapportini.forEach(r => {
        const key = `${r.dataCorretta}-${r.cantiereId}`;
        
        if (!rapportiniAggregati[key]) {
          rapportiniAggregati[key] = {
            dataCorretta: r.dataCorretta,
            cantierNome: r.cantierNome,
            ore: 0
          };
        }
        
        rapportiniAggregati[key].ore += parseFloat(r.mezzo.ore || r.mezzo.oreLavorate || 0);
      });
      
      // Converti in array e ordina per data
      const rapportiniUnici = Object.values(rapportiniAggregati).sort((a, b) => 
        a.dataCorretta.localeCompare(b.dataCorretta)
      );
      
      const dettaglioData = rapportiniUnici.map(r => {
        // Converti da dataCorretta (YYYY-MM-DD) a formato italiano
        let dataFormattata = 'Data non disponibile';
        
        if (r.dataCorretta && typeof r.dataCorretta === 'string') {
          const parti = r.dataCorretta.split('-');
          if (parti.length === 3) {
            const [anno, mese, giorno] = parti;
            dataFormattata = `${giorno}/${mese}/${anno}`;
          }
        }
        
        return [
          dataFormattata,
          r.cantierNome,
          r.ore.toFixed(1) + ' h'
        ];
      });

      autoTable(pdf, {
        startY: yPos,
        head: [['Data', 'Cantiere', 'Ore']],
        body: dettaglioData,
        theme: 'striped',
        headStyles: { fillColor: [0, 123, 255], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 120 },
          2: { cellWidth: 35, halign: 'right' }
        }
      });

      // Footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Pagina ${i} di ${pageCount} - Generato il ${new Date().toLocaleDateString('it-IT')}`,
          pageWidth / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Salva PDF
      const nomeFile = `Storico_Utilizzo_${selectedMezzo.nomeMezzo.replace(/\s+/g, '_')}_${dataInizioUtilizzo}_${dataFineUtilizzo}.pdf`;
      pdf.save(nomeFile);

      alert('✅ PDF generato con successo!');
      closeStoricoUtilizzoModal();

    } catch (error) {
      console.error("Errore nella generazione PDF:", error);
      alert('❌ Errore nella generazione del PDF: ' + error.message);
    } finally {
      setLoadingPDF(false);
    }
  };

  // Funzione per convertire logo in base64
  const getLogoBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = this.width;
        canvas.height = this.height;
        ctx.drawImage(this, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = logo;
    });
  };

  if (isLoading && mezzi.length === 0) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Caricamento mezzi...</p>
      </div>
    );
  }

  return (
    <div className="mezzi-management-container">
      <div className="mezzi-header">
        <h2>🚛 Gestione Parco Mezzi</h2>
        <div className="mezzi-header-actions">
          <button 
            className="btn-create"
            onClick={() => setShowForm(true)}
            disabled={isLoading}
          >
            ➕ Nuovo Mezzo
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="mezzi-filters">
        <div className="filter-group">
          <label>🔍 Cerca</label>
          <input
            type="text"
            placeholder="Cerca per nome, targa o note..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>🏢 Proprietà</label>
          <select
            value={filterProprieta}
            onChange={(e) => setFilterProprieta(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tutte</option>
            {proprietaOptions.map(prop => (
              <option key={prop} value={prop}>{prop}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Griglia mezzi */}
      {filteredMezzi.length === 0 ? (
        <div className="empty-state">
          <p>📋 Nessun mezzo trovato</p>
        </div>
      ) : (
        <div className="mezzi-grid">
          {filteredMezzi.map(mezzo => (
            <div key={mezzo.id} className="mezzo-card">
              <div className="mezzo-card-header">
                <h3>{mezzo.nomeMezzo}</h3>
                {mezzo.targa && (
                  <span className="mezzo-targa">{mezzo.targa}</span>
                )}
              </div>

              <div className="mezzo-card-body">
                <div className="mezzo-info">
                  <span className="info-label">🏢 Proprietà:</span>
                  <span className="info-value">{mezzo.proprieta || 'N/D'}</span>
                </div>

                {mezzo.note && (
                  <div className="mezzo-info">
                    <span className="info-label">📝 Note:</span>
                    <span className="info-value">{mezzo.note}</span>
                  </div>
                )}
              </div>

              <div className="mezzo-card-actions">
                <button
                  onClick={() => editMezzo(mezzo)}
                  className="btn-edit"
                  title="Modifica mezzo"
                >
                Modifica
                </button>
                <button
                  onClick={() => openStoricoUtilizzoModal(mezzo)}
                  className="btn-storico"
                  title="Storico utilizzo cantieri"
                >
                Storico Utilizzo
                </button>
                <button
                  onClick={() => openStoricoManutenzioniModal(mezzo)}
                  className="btn-manutenzioni"
                  title="Storico manutenzioni"
                >
                Manutenzioni
                </button>
                <button
                  onClick={() => deleteMezzo(mezzo.id, mezzo.nomeMezzo)}
                  className="btn-delete"
                  title="Elimina mezzo"
                >
                Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form Creazione/Modifica Mezzo */}
      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content mezzi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? '✏️ Modifica Mezzo' : '➕ Nuovo Mezzo'}</h3>
              <button onClick={resetForm} className="modal-close">✕</button>
            </div>

            <form onSubmit={saveMezzo} className="mezzi-form">
              <div className="form-group">
                <label>Nome Mezzo *</label>
                <input
                  type="text"
                  name="nomeMezzo"
                  value={formData.nomeMezzo}
                  onChange={handleInputChange}
                  required
                  placeholder="Es. FIAT SCUDO"
                />
              </div>

              <div className="form-group">
                <label>Targa</label>
                <input
                  type="text"
                  name="targa"
                  value={formData.targa}
                  onChange={handleInputChange}
                  placeholder="Es. FC002HC"
                />
              </div>

              <div className="form-group">
                <label>Proprietà</label>
                <select
                  name="proprieta"
                  value={formData.proprieta}
                  onChange={handleInputChange}
                >
                  <option value="">Seleziona proprietà</option>
                  {proprietaOptions.map(prop => (
                    <option key={prop} value={prop}>{prop}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Note</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Note aggiuntive..."
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Annulla
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {editingId ? '💾 Aggiorna' : '➕ Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Storico UTILIZZO CANTIERI */}
      {showStoricoUtilizzoModal && selectedMezzo && (
        <div className="modal-overlay" onClick={closeStoricoUtilizzoModal}>
          <div className="modal-content storico-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 Storico Utilizzo - {selectedMezzo.nomeMezzo}</h3>
              <button onClick={closeStoricoUtilizzoModal} className="modal-close">✕</button>
            </div>
            
            <div className="modal-body">
              <div className="storico-info">
                <p><strong>Mezzo:</strong> {selectedMezzo.nomeMezzo}</p>
                {selectedMezzo.targa && <p><strong>Targa:</strong> {selectedMezzo.targa}</p>}
                <p><strong>Proprietà:</strong> {selectedMezzo.proprieta || 'N/D'}</p>
              </div>

              <div className="date-filters">
                <div className="filter-group">
                  <label>📅 Data Inizio</label>
                  <input
                    type="date"
                    value={dataInizioUtilizzo}
                    onChange={(e) => setDataInizioUtilizzo(e.target.value)}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>📅 Data Fine</label>
                  <input
                    type="date"
                    value={dataFineUtilizzo}
                    onChange={(e) => setDataFineUtilizzo(e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="storico-description">
                <p>Il report PDF conterrà:</p>
                <ul>
                  <li>📊 Riepilogo per cantiere (giorni e ore lavorate)</li>
                  <li>📅 Dettaglio giornaliero completo</li>
                  <li>⏱️ Totali periodo selezionato</li>
                </ul>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={closeStoricoUtilizzoModal}
                disabled={loadingPDF}
              >
                Annulla
              </button>
              <button 
                type="button" 
                className="btn-save" 
                onClick={generaStoricoPDF}
                disabled={loadingPDF || !dataInizioUtilizzo || !dataFineUtilizzo}
              >
                {loadingPDF ? '⏳ Generazione...' : '📥 Scarica PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Storico MANUTENZIONI */}
      {showStoricoManutenzioniModal && selectedMezzo && (
        <div className="modal-overlay" onClick={closeStoricoManutenzioniModal}>
          <div className="modal-content storico-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔧 Storico Manutenzioni - {selectedMezzo.nomeMezzo}</h3>
              <button onClick={closeStoricoManutenzioniModal} className="modal-close">✕</button>
            </div>

            <div className="modal-body">
              <div className="storico-info">
                <p><strong>Mezzo:</strong> {selectedMezzo.nomeMezzo}</p>
                {selectedMezzo.targa && <p><strong>Targa:</strong> {selectedMezzo.targa}</p>}
              </div>

              <div className="date-filters">
                <div className="filter-group">
                  <label>📅 Data Inizio</label>
                  <input
                    type="date"
                    value={dataInizioManutenzioni}
                    onChange={(e) => setDataInizioManutenzioni(e.target.value)}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>📅 Data Fine</label>
                  <input
                    type="date"
                    value={dataFineManutenzioni}
                    onChange={(e) => setDataFineManutenzioni(e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="form-actions" style={{justifyContent: 'center', marginBottom: '20px'}}>
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={caricaStoricoManutenzioni}
                  disabled={loadingStoricoManutenzioni || !dataInizioManutenzioni || !dataFineManutenzioni}
                >
                  {loadingStoricoManutenzioni ? '⏳ Caricamento...' : '🔍 Cerca Manutenzioni'}
                </button>
              </div>

              {loadingStoricoManutenzioni ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Caricamento storico...</p>
                </div>
              ) : storicoManutenzioni.length === 0 ? (
                <div className="empty-state">
                  <p>📋 Nessuna manutenzione registrata per questo mezzo nel periodo selezionato</p>
                </div>
              ) : (
                <div className="storico-table-container">
                  <table className="storico-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo Lavorazione</th>
                        <th>Tempo Impiegato</th>
                        <th>Personale</th>
                        <th>Km</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storicoManutenzioni.map((manutenzione, index) => (
                        <tr key={index}>
                          <td>
                            {manutenzione.data.toLocaleDateString('it-IT')}
                          </td>
                          <td>{manutenzione.tipoLavorazione}</td>
                          <td>{manutenzione.tempoImpiegato} ore</td>
                          <td>
                            {manutenzione.personaleImpiegato.length > 0 
                              ? manutenzione.personaleImpiegato.map(p => 
                                  `${p.nome} ${p.cognome}`
                                ).join(', ')
                              : 'N/D'
                            }
                          </td>
                          <td>{manutenzione.km}</td>
                          <td>{manutenzione.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeStoricoManutenzioniModal} className="btn-secondary">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapoOfficinaMezzi;